import { USER_ACTIVATION_METHOD } from '../../domain/domainCodes.js'
import { ApiError, api } from '../../lib/api'
import { licenseManagementStrings } from './strings'

/**
 * Organisation endpoints.
 *
 *   listCompanies    -> GET  /api/admin/manage-companies/        REAL
 *   listCompanyUsers -> POST /ManageCompanies/GetUsers { Id }    REAL
 *   getCompany       -> GET  /api/company-profile?companyId=
 *   updateCompany    -> POST /CompanyProfile/Update
 *   listLicenses     -> GET  LicenseManagement/Index             still mock
 *   saveLicense      -> POST LicenseManagement/Save              still mock
 *   getLicenseInfo   -> GET  LicenseInformation/Index            still mock
 *
 * DELETING AN ORGANISATION IS NOT OFFERED, and the endpoint is the reason rather than the
 * absence of one. `DeleteCompanyHandler` hardcodes `var canBeDeleted = false;` — with a comment
 * saying the legacy controller did the same — so the call can only ever refuse. Worse, it reaches
 * that line only after loading the company with EIGHT `.Include()` collections; against a real
 * tenant the request does not come back. API_FINDINGS.md §67.
 */
const COMPANIES = '/api/admin/manage-companies'
const ACTIONS = '/ManageCompanies'

/**
 * Every organisation on the installation.
 *
 * `SystemManagers` only — this is the CoEIA-side tenant list, not something a customer admin sees.
 */
export async function listCompanies(signal) {
  const dto = (await api.get(`${COMPANIES}/`, { signal })) ?? {}

  return {
    rows: (dto.Companies ?? []).map((row) => ({
      id: row.CompanyId,
      arabicName: row.ArabicName ?? '',
      englishName: row.EnglishName ?? '',
      email: row.Email ?? '',
      telephone: row.Telephone ?? '',
      /* A real `DateTime` here, not a pre-formatted string — this slice predates the habit. ISO
         on the wire; the screen shows the date part and never re-formats the rest. */
      registeredOn: (row.RegisterDate ?? '').slice(0, 10),
      isActive: Boolean(row.IsActive),
    })),
  }
}

/**
 * The people inside one organisation.
 *
 * A JSON body, `{ Id }`, bound with `[FromBody]` — so a form post is a 415. On failure the
 * endpoint answers an EMPTY ARRAY with HTTP 200 rather than an error, which is indistinguishable
 * from an organisation that genuinely has nobody in it.
 */
export async function listCompanyUsers(companyId, signal) {
  const rows = (await api.post(`${ACTIONS}/GetUsers`, { Id: Number(companyId) }, { signal })) ?? []

  return {
    rows: rows.map((row) => ({
      userId: row.UserId,
      username: row.Username ?? '',
      arabicName: row.ArabicName ?? '',
      englishName: row.EnglishName ?? '',
      /* One row in 155 on the demo database holds binary bytes here instead of an address —
         stored that way, not a decoding fault on the way out. Rendered as the text it is. */
      email: row.Email ?? '',
      phone: row.PhoneNumber ?? '',
      groupId: row.GroupId ?? null,
      isActive: Boolean(row.IsActive),
    })),
  }
}

/**
 * One organisation's profile — `Features/Admin/CompanyProfile/Endpoint.cs`.
 *
 *   getCompany    -> GET  /api/company-profile?companyId=
 *   updateCompany -> POST /CompanyProfile/Update   { CompanyId, ArabicName, … }
 *
 * The `companyId` is a REQUEST, not an instruction: only a CoEIA admin may look at
 * another organisation, and for everyone else the handler ignores it and uses the company
 * on their own claim. Asking for someone else's simply returns your own.
 *
 * A CoEIA admin with no company of their own gets `{ RedirectTo }` instead of a profile —
 * the page route used to answer a 302 for that, which a `fetch` cannot follow usefully.
 */
export async function getCompany(companyId, signal) {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : ''
  const dto = (await api.get(`/api/company-profile${query}`, { signal })) ?? {}

  if (dto.RedirectTo) return { redirectTo: dto.RedirectTo, company: null, authMethods: [] }

  return {
    company: {
      id: dto.CompanyId,
      name: dto.CompanyName ?? '',
      arabicName: dto.ArabicName ?? '',
      englishName: dto.EnglishName ?? '',
      email: dto.Email ?? '',
      telephone: dto.Telephone ?? '',
      supportEmail: dto.SupportEmail ?? '',
      supportPhone: dto.SupportPhone ?? '',
      companyDomains: dto.CompanyDomains ?? '',
      activateByEmail: Boolean(dto.ActivateUserByEmail),
      activateByAdmin: Boolean(dto.ActivateUserByAdmin),
      userEmailOnDomain: Boolean(dto.UserEmailOnDomain),
      authenticationId: dto.AuthenticationId ?? '',
      licenseType: dto.LicenseType ?? '',
      signupUrl: dto.SignupUrl ?? '',
    },
    authMethods: (dto.AuthMethodList ?? []).map((method) => ({
      id: method.AuthenticationId,
      name: method.AuthenticationName ?? '',
    })),
    isCoeiaAdmin: Boolean(dto.IsCoeiaAdmin),
  }
}

export async function updateCompany(companyId, values) {
  try {
    const data = await api.post('/CompanyProfile/Update', {
      CompanyId: Number(companyId),
      ArabicName: values.arabicName ?? '',
      EnglishName: values.englishName ?? '',
      Email: values.email ?? '',
      SupportEmail: values.supportEmail ?? '',
      SupportPhone: values.supportPhone ?? '',
      CompanyDomains: values.companyDomains ?? '',
      /* One radio in the UI, two booleans on the wire: '2' is "an administrator approves",
         anything else is "the user confirms by email". They are mutually exclusive. */
      ActivateUserByAdmin: String(values.activationBy) === String(USER_ACTIVATION_METHOD.ADMIN),
      ActivateUserByEmail: String(values.activationBy) !== String(USER_ACTIVATION_METHOD.ADMIN),
      UserEmailOnDomain: Boolean(values.userEmailOnDomain),
      AuthenticationId: values.authenticationId ? Number(values.authenticationId) : null,
    })
    return { ok: true, message: data?.Message ?? '' }
  } catch (error) {
    /* The command is FluentValidation-backed — a missing name or a malformed address
       comes back as its rule's message, which is more use than "could not save". */
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/* Scaffolding for the three licence mocks below only — nothing above uses it. They belong to
   `LicenseManagement`, a slice of its own that has not been integrated. */
const NETWORK_DELAY = 220
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
const load = () => import('../../data/organisations')

/**
 * The legacy lists one row per company with an active licence. Type, seats, start date
 * and duration come out of the encrypted LicenseData blob, so they arrive as null and
 * the table shows the placeholder.
 */
/**
 * The licence book — REAL organisations, and no invented licences.
 *
 * There is no endpoint behind this screen (see the header). It used to answer entirely from
 * the bundled fixture, which drew a summary strip and twelve client rows — أرامكو السعودية,
 * مصرف الراجحي, جامعة الملك سعود and nine more — none of which exist on this installation:
 * `GET /api/admin/manage-companies/` returns one organisation.
 *
 * The organisations now come from that real endpoint, so the screen can no longer name
 * clients who are not there. The licence ROWS stay empty because nothing serves them, which
 * is the honest state for a screen with no backend — see COSMETIC_UI.md.
 */
export async function listLicenses(signal) {
  const { licenseTypes } = await load()
  const companiesResult = await listCompanies(signal).catch(() => ({ rows: [] }))

  return { rows: [], companies: companiesResult.rows ?? [], licenseTypes }
}

/**
 * NO ENDPOINT — so this refuses rather than pretending.
 *
 * It used to `return { ok: true }` after a delay, so the create form raised a genuine
 * success dialog ("تمت العملية بنجاح") without a single request leaving the browser. That is
 * the shape `API_FINDINGS.md` §89 was opened for, and `ClientApp/CLAUDE.md` is explicit:
 * nothing ships that looks functional and silently does nothing.
 *
 * `LicenseManagement/Save` is the route this will call once the slice is integrated.
 */
export async function saveLicense() {
  throw new ApiError(licenseManagementStrings.saveUnavailable, { status: 0, code: 'RESULT' })
}

export async function getLicenseInfo() {
  await delay(NETWORK_DELAY)
  const { companies, licenses, licenseHistory, licenseTypes } = await load()
  const license = licenses[0] ?? null
  const nameOf = (id) => licenseTypes.find((type) => type.id === id)?.name ?? ''

  return {
    company: companies.find((row) => row.id === license?.companyId) ?? null,
    license: license && { ...license, licenseTypeName: nameOf(license.licenseType) },
    /* This view names the same two columns `duration` and `accounts`. */
    history: licenseHistory
      .filter((row) => row.licenseId === license?.id)
      .map((row) => ({
        ...row,
        licenseType: nameOf(row.licenseType),
        duration: row.durationDays,
        accounts: row.totalAccounts,
      })),
  }
}
